import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import VerdictTrade from "@/components/VerdictTrade";
import {
  ActivitySquare,
  ArrowDownRight,
  ArrowUpRight,
  Loader2,
  PlayCircle,
  Sparkles,
} from "lucide-react";
import {
  getQuote,
  startAnalysis,
  getAnalysis,
} from "@/lib/api";

const RATING_COLOR = {
  BUY: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  OVERWEIGHT: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
  HOLD: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  UNDERWEIGHT: "text-orange-300 border-orange-400/30 bg-orange-400/10",
  SELL: "text-red-400 border-red-500/30 bg-red-500/10",
};

const fmt = (n, digits = 2) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  // Intl requires maximumFractionDigits >= minimumFractionDigits, otherwise it
  // throws RangeError (happens for |v| < 1 where min was bumped to 4).
  const minDigits = Math.abs(v) < 1 ? 4 : 2;
  return v.toLocaleString(undefined, {
    maximumFractionDigits: Math.max(digits, minDigits),
    minimumFractionDigits: minDigits,
  });
};

// Strip markdown bold/heading markers and normalize bullets so agent
// output reads cleanly inside a monospace <pre>. Not a real MD renderer —
// intentionally lightweight.
const prettifyAgentText = (text) => {
  if (!text) return "";
  return text
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*\*\s+/gm, "• ")
    .replace(/^\s*-\s+/gm, "• ");
};

export default function PocPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSymbol = searchParams.get("symbol") || "AAPL";
  const deepLinkRunId = searchParams.get("run");
  const [symbol, setSymbol] = useState(initialSymbol);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteErr, setQuoteErr] = useState(null);

  const [runId, setRunId] = useState(deepLinkRunId || null);
  const [run, setRun] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  // Auto-load a quote on mount for the default symbol — but skip if we're
  // hydrating a deep-linked run; that effect will fetch the correct quote.
  useEffect(() => {
    if (deepLinkRunId) return;
    fetchQuote(initialSymbol);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If arrived via /analyze?run=<id>, hydrate the run so the "VERDICT ↗"
  // deep links from portfolio trade history work.
  useEffect(() => {
    if (!deepLinkRunId) return;
    (async () => {
      try {
        const doc = await getAnalysis(deepLinkRunId);
        setRun(doc);
        if (doc?.symbol && doc.symbol !== symbol) {
          setSymbol(doc.symbol);
          fetchQuote(doc.symbol);
        }
      } catch (e) {
        toast.error("Could not load that run.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkRunId]);

  // Poll analysis every 3s while running
  useEffect(() => {
    if (!runId) return undefined;
    const tick = async () => {
      try {
        const doc = await getAnalysis(runId);
        setRun(doc);
        if (doc.status !== "running") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (doc.status === "completed") {
            toast.success(`Analysis complete for ${doc.symbol}`);
          } else if (doc.status === "failed") {
            toast.error(`Analysis failed: ${doc.error || "unknown error"}`);
          }
        }
      } catch (e) {
        // keep polling — transient error
        console.error(e);
      }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [runId]);

  const fetchQuote = async (raw) => {
    const s = (raw || symbol || "").trim().toUpperCase();
    if (!s) return;
    setQuoteLoading(true);
    setQuoteErr(null);
    try {
      const q = await getQuote(s);
      setQuote(q);
    } catch (e) {
      setQuote(null);
      const msg = e?.response?.data?.detail || e?.message || "Failed to fetch quote";
      setQuoteErr(msg);
      toast.error(msg);
    } finally {
      setQuoteLoading(false);
    }
  };

  const handleAnalyze = async () => {
    const s = (symbol || "").trim().toUpperCase();
    if (!s) return;
    setStarting(true);
    setRun(null);
    // Clear any stale query params (e.g. ?run=... from a deep-link) so the
    // hydration effect above doesn't rehydrate the previous run's state, and
    // refresh the quote so we don't display a stale symbol next to the new
    // verdict.
    if (searchParams.get("run") || searchParams.get("symbol")) {
      setSearchParams({}, { replace: true });
    }
    fetchQuote(s);
    try {
      const { run_id } = await startAnalysis(s);
      setRunId(run_id);
      toast.info(`Analysis started for ${s}`, {
        description: "Six agents will report progressively.",
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to start analysis";
      toast.error(msg);
    } finally {
      setStarting(false);
    }
  };

  const changePositive = quote?.change != null && quote.change >= 0;
  const isRunning = run?.status === "running" || (runId && !run);

  return (
    <div>
      <div>
        {/* Header note */}
        <header className="mb-8 flex items-baseline justify-between">
          <div>
            <h1 className="text-xs uppercase tracking-widest text-[var(--aurion-muted)]">
              Analyze a ticker
            </h1>
            <div className="mt-1 text-2xl text-[var(--aurion-text)]">
              Six AI agents debate any US stock or major crypto
            </div>
          </div>
          <div className="hidden items-center gap-2 text-xs text-[var(--aurion-muted)] md:flex">
            <ActivitySquare className="h-4 w-4" />
            <span>yfinance · Gemini 3 Flash · Claude Sonnet 4.6</span>
          </div>
        </header>

        {/* Symbol + Actions */}
        <Card className="border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label
                htmlFor="symbol-input"
                className="mb-2 block text-xs uppercase tracking-widest text-[var(--aurion-muted)]"
              >
                Symbol (try AAPL, NVDA, TSLA, BTC-USD, ETH-USD)
              </label>
              <Input
                id="symbol-input"
                data-testid="symbol-input"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === "Enter") fetchQuote();
                }}
                placeholder="AAPL"
                className="h-12 border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] font-mono text-lg tracking-wider text-[var(--aurion-text)] focus-visible:ring-[var(--aurion-gold)]"
              />
            </div>
            <div className="flex gap-3">
              <Button
                data-testid="quote-btn"
                onClick={() => fetchQuote()}
                disabled={quoteLoading}
                variant="outline"
                className="h-12 border-[var(--aurion-border)] bg-transparent text-[var(--aurion-text)] hover:bg-[var(--aurion-panel-2)]"
              >
                {quoteLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Fetch quote
              </Button>
              <Button
                data-testid="run-analysis-btn"
                onClick={handleAnalyze}
                disabled={starting || isRunning}
                className="h-12 border border-[var(--aurion-gold)] bg-[var(--aurion-gold)]/10 font-semibold tracking-wider text-[var(--aurion-gold)] hover:bg-[var(--aurion-gold)]/20"
              >
                {starting || isRunning ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <PlayCircle className="mr-2 h-4 w-4" />
                )}
                Run analysis
              </Button>
            </div>
          </div>
        </Card>

        {/* Quote Display */}
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Card
            data-testid="quote-card"
            className="col-span-1 border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-6 lg:col-span-1"
          >
            <div className="text-xs uppercase tracking-widest text-[var(--aurion-muted)]">
              Latest quote
            </div>
            {quoteErr ? (
              <div
                data-testid="quote-error"
                className="mt-4 text-sm text-[var(--aurion-red)]"
              >
                {quoteErr}
              </div>
            ) : quote ? (
              <div className="mt-3">
                <div
                  className="text-2xl font-semibold text-[var(--aurion-text)]"
                  data-testid="quote-symbol"
                >
                  {quote.symbol}
                </div>
                <div className="text-xs text-[var(--aurion-muted)]">{quote.name}</div>

                <div className="mt-5 flex items-baseline gap-3">
                  <div
                    className="font-mono text-4xl tracking-tight"
                    style={{ color: "var(--aurion-gold)" }}
                    data-testid="quote-price"
                  >
                    {fmt(quote.price)}
                  </div>
                  <div className="text-xs text-[var(--aurion-muted)]">
                    {quote.currency}
                  </div>
                </div>

                <div
                  className={`mt-2 flex items-center gap-1 text-sm ${
                    changePositive ? "text-emerald-400" : "text-red-400"
                  }`}
                  data-testid="quote-change"
                >
                  {changePositive ? (
                    <ArrowUpRight className="h-4 w-4" />
                  ) : (
                    <ArrowDownRight className="h-4 w-4" />
                  )}
                  {quote.change !== null ? fmt(quote.change) : "—"}
                  <span className="ml-1 text-xs opacity-80">
                    ({quote.change_pct !== null ? fmt(quote.change_pct) : "—"}%)
                  </span>
                </div>

                <Separator className="my-4 bg-[var(--aurion-border)]" />
                <div className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                  As of
                </div>
                <div className="text-xs text-[var(--aurion-muted)]">
                  {new Date(quote.timestamp).toLocaleString()}
                </div>
              </div>
            ) : (
              <div className="mt-6 text-sm text-[var(--aurion-muted)]">
                {quoteLoading ? "Loading…" : "No quote yet."}
              </div>
            )}
          </Card>

          {/* Verdict card */}
          <Card
            data-testid="verdict-card"
            className="col-span-1 border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-6 lg:col-span-2"
          >
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-[var(--aurion-muted)]">
                Portfolio Manager Verdict
              </div>
              {run?.status ? <StatusBadge status={run.status} /> : null}
            </div>

            {run?.verdict ? (
              <div className="mt-5">
                <div className="flex flex-wrap items-baseline gap-4">
                  <span
                    data-testid="verdict-rating"
                    className={`rounded-none border px-3 py-1 text-lg font-semibold tracking-widest ${
                      RATING_COLOR[run.verdict.rating] || RATING_COLOR.HOLD
                    }`}
                  >
                    {run.verdict.rating}
                  </span>
                  <div className="font-mono text-2xl text-[var(--aurion-text)]">
                    <span
                      style={{ color: "var(--aurion-gold)" }}
                      data-testid="verdict-conviction"
                    >
                      {run.verdict.conviction}
                    </span>
                    <span className="text-sm text-[var(--aurion-muted)]">
                      /100 conviction
                    </span>
                  </div>
                  <div className="font-mono text-sm text-[var(--aurion-muted)]">
                    target alloc:{" "}
                    <span
                      className="text-[var(--aurion-text)]"
                      data-testid="verdict-target-alloc"
                    >
                      {run.verdict.target_allocation_pct}%
                    </span>
                  </div>
                </div>
                <p
                  data-testid="verdict-rationale"
                  className="mt-4 text-sm leading-relaxed text-[var(--aurion-text)]"
                >
                  {run.verdict.rationale}
                </p>
                {run.duration_ms ? (
                  <div className="mt-4 text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                    Run duration · {(run.duration_ms / 1000).toFixed(1)}s
                  </div>
                ) : null}
                <VerdictTrade run={run} />
              </div>
            ) : run?.error ? (
              <div className="mt-5 text-sm text-red-400" data-testid="verdict-error">
                {run.error}
              </div>
            ) : (
              <div className="mt-5 flex items-center gap-2 text-sm text-[var(--aurion-muted)]">
                <Sparkles className="h-4 w-4 opacity-60" />
                {isRunning
                  ? "Six agents are debating this ticker…"
                  : "Run an analysis to see the verdict."}
              </div>
            )}
          </Card>
        </div>

        {/* Transcript */}
        <div className="mt-6">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm uppercase tracking-widest text-[var(--aurion-muted)]">
              Agent transcript
            </h2>
            {isRunning ? (
              <div className="flex items-center gap-2 text-xs text-[var(--aurion-gold)]">
                <span className="aurion-pulse inline-block h-2 w-2 rounded-full bg-[var(--aurion-gold)]" />
                live · polling every 3s
              </div>
            ) : null}
          </div>

          <Card
            data-testid="transcript-container"
            className="aurion-scroll max-h-[600px] overflow-y-auto border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-2"
          >
            {run?.transcript?.length ? (
              <ul className="divide-y divide-[var(--aurion-border)]">
                {run.transcript.map((entry, i) => (
                  <li
                    key={i}
                    className="px-4 py-4"
                    data-testid={`transcript-entry-${entry.role}`}
                  >
                    <div className="mb-2 flex items-center gap-2">
                      <span
                        className="text-[10px] font-semibold uppercase tracking-widest"
                        style={{
                          color:
                            entry.role === "system"
                              ? "var(--aurion-muted)"
                              : "var(--aurion-gold)",
                        }}
                      >
                        {entry.agent}
                      </span>
                      <span className="text-[10px] text-[var(--aurion-muted)]">
                        {new Date(entry.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <pre className="whitespace-pre-wrap font-mono text-[13px] leading-relaxed text-[var(--aurion-text)]">
                      {prettifyAgentText(entry.content)}
                    </pre>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-6 text-sm text-[var(--aurion-muted)]">
                {isRunning
                  ? "Waiting for the first agent to check in…"
                  : "Run an analysis to see the transcript here."}
              </div>
            )}
          </Card>
        </div>

        <footer className="mt-10 text-center text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
          Paper trading only · not investment advice
        </footer>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    running:
      "border-[var(--aurion-gold)]/40 text-[var(--aurion-gold)] bg-[var(--aurion-gold)]/10",
    completed: "border-emerald-500/40 text-emerald-400 bg-emerald-500/10",
    failed: "border-red-500/40 text-red-400 bg-red-500/10",
  };
  return (
    <span
      data-testid="run-status"
      className={`rounded-none border px-2 py-0.5 text-[10px] uppercase tracking-widest ${
        styles[status] || styles.running
      }`}
    >
      {status}
    </span>
  );
}
